import { useEffect, useMemo, useState } from "react";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2, MapPin, Navigation } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/i18n";

const icon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

/** Interactive OpenStreetMap pin for a listing. Falls back to district+state when
 *  no street address is on file. */
export const BusinessMap = ({ listing }) => {
  const t = useT();
  const [point, setPoint] = useState(null);
  const [status, setStatus] = useState("loading");
  const [level, setLevel] = useState("address");

  const parts = useMemo(
    () =>
      [
        listing.street_address,
        [listing.postcode, listing.district].filter(Boolean).join(" "),
        listing.state,
        "Malaysia",
      ].filter(Boolean),
    [listing.street_address, listing.postcode, listing.district, listing.state]
  );

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setPoint(null);

    api
      .get(`/listings/${listing.id}/location`)
      .then(({ data }) => {
        if (cancelled) return;
        setPoint({ lat: data.lat, lon: data.lon, zoom: data.zoom });
        setLevel(data.matched_level);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("failed");
      });

    return () => {
      cancelled = true;
    };
  }, [listing.id]);

  const usedFallback = level !== "address" && level !== "postcode";
  const position = useMemo(() => (point ? [point.lat, point.lon] : null), [point]);

  return (
    <section className="mt-14" data-testid="business-map-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">{t("map.location")}</h2>
          <p data-testid="map-address-line" className="mt-2 flex items-start gap-1.5 text-sm text-slate-600">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{parts.join(", ")}</span>
          </p>
        </div>
      </div>

      {point && (
        <div className="mt-4 flex flex-wrap gap-3" data-testid="map-directions">
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${point.lat},${point.lon}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="directions-google-button"
            className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-slate-800"
          >
            <Navigation className="h-4 w-4" /> {t("map.directions")}
          </a>
          <a
            href={`https://waze.com/ul?ll=${point.lat},${point.lon}&navigate=yes`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="directions-waze-button"
            className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm font-semibold text-slate-800 transition-colors duration-200 hover:bg-slate-900 hover:text-white"
          >
            <Navigation className="h-4 w-4" /> Waze
          </a>
        </div>
      )}

      <div className="mt-5 overflow-hidden rounded-lg border border-border bg-slate-100">
        {status === "loading" && (
          <div data-testid="map-loading" className="grid h-72 place-items-center text-slate-500 sm:h-80">
            <span className="flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> {t("map.loading")}
            </span>
          </div>
        )}

        {status === "failed" && (
          <div data-testid="map-unavailable" className="grid h-72 place-items-center px-6 text-center sm:h-80">
            <p className="text-sm text-slate-500">
              {t("map.unavailable")}
            </p>
          </div>
        )}

        {status === "ready" && point && (
          <MapContainer
            key={`${point.lat}-${point.lon}`}
            center={position}
            zoom={point.zoom || 15}
            scrollWheelZoom={false}
            style={{ height: "20rem", width: "100%" }}
            data-testid="business-map"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={position} icon={icon}>
              <Popup>
                <strong>{listing.business_name}</strong>
                <br />
                {parts.join(", ")}
              </Popup>
            </Marker>
          </MapContainer>
        )}
      </div>

      {status === "ready" && usedFallback && (
        <p data-testid="map-fallback-note" className="mt-3 text-xs text-slate-500">
          Peta dipusatkan pada {listing.district}, {listing.state} kerana alamat penuh tidak tersedia.
        </p>
      )}
    </section>
  );
};
